// src/Screens/HowToPlay.jsx
// A genuinely new, indexable page — search demand for "how to play name
// place animal thing" / rules / scoring intent has nowhere to land today;
// every other route is a functional app screen, not explanatory content.
import { useNavigate } from "react-router-dom";
import SeoHead from "../Components/SeoHead";

const FAQS = [
  {
    q: "How does scoring work?",
    a: "With friends, a unique answer (one no other player wrote for that category) scores 10 points, an answer shared with another player scores 5, and a blank or invalid answer scores 0. Solo scores differently: a valid answer earns up to 15 points, more for rarer answers, and half if you and the bot wrote the same thing.",
  },
  {
    q: "Can I play with custom categories?",
    a: "Yes. Any host can replace or add categories beyond the default four (Name, Animal, Place, Thing) when creating a room.",
  },
  {
    q: "Do I need to download anything?",
    a: "No. NameIt runs entirely in your browser on any device — no app install required to play with friends or solo against AI.",
  },
  {
    q: "Can I play alone?",
    a: "Yes. Play Solo lets you race one of three AI opponents — Kafi, Kosi, or Kemi — in a quick 5-round game or the full A–Z challenge.",
  },
  {
    q: "What are Special Rounds?",
    a: "Optional surprise twist rounds a host can enable that modify scoring or add new challenges, drawn randomly throughout a game.",
  },
];

export default function HowToPlay() {
  const navigate = useNavigate();

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      <SeoHead
        title="How to Play Name, Place, Animal, Thing: Rules and Scoring"
        description="Learn the rules, scoring, answer-review process, and category ideas for Name, Place, Animal, Thing, then start a free online game."
        path="/how-to-play"
      />
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <header className="bg-gradient-to-br from-orange-50 to-red-50 border-b border-red-100 shadow-sm px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/70 hover:bg-white transition shadow-sm"
        >
          <span className="text-gray-600 text-sm">←</span>
        </button>
        <span className="text-lg font-black text-red-700">NameIt!</span>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl sm:text-4xl font-black text-red-700 tracking-tight mb-3">
          How to Play Name, Place, Animal, Thing
        </h1>
        <p className="text-gray-600 leading-relaxed mb-8">
          Name, Place, Animal, Thing (also known as Categories or Scattergories in some
          regions) is the classic category word game — now free to play online, with
          friends or solo against AI, and no download required.
        </p>

        <section className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">The rules</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 text-sm leading-relaxed">
            <li>Each round, a random letter is drawn (for example, "M").</li>
            <li>
              Every player races to fill in each category — a Name, Animal, Place, and
              Thing all starting with that letter.
            </li>
            <li>The first player to finish can stop the round for everyone.</li>
            <li>Players review and vote on each other's answers to check they're valid.</li>
            <li>The game continues for multiple rounds; the player with the most points wins.</li>
          </ol>
        </section>

        <section className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Scoring</h2>
          <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
            <li>
              <span className="font-semibold text-green-700">10 points</span> — a unique
              answer nobody else wrote for that category.
            </li>
            <li>
              <span className="font-semibold text-amber-600">5 points</span> — an answer
              shared with another player.
            </li>
            <li>
              <span className="font-semibold text-gray-600">0 points</span> — a blank or
              invalid answer.
            </li>
          </ul>
          <p className="text-gray-700 text-sm leading-relaxed mt-3">
            Players vote on whether each answer counts. If the vote is tied, a coin flip
            decides, and the answer is marked so everyone can see.
          </p>
          <h3 className="text-base font-bold text-gray-900 mt-5 mb-2">Solo scoring</h3>
          <p className="text-gray-700 text-sm leading-relaxed">
            Against Kafi, Kosi or Kemi, each valid answer scores up to{" "}
            <span className="font-semibold text-green-700">15 points</span>. The rarer the
            answer, the more it scores, and harder bots are stricter about common answers.
            If you and the bot write the same answer, you both get half.
          </p>
        </section>

        <section className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Ways to play</h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-3">
            Create a private room and share the link with friends, join a public room to
            play with others online, or race an AI opponent solo — Kafi (easy), Kosi
            (medium), or Kemi (hard) — in a quick 5-round game or the full 26-round A–Z
            challenge.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/")}
              className="flex-1 py-3 rounded-xl font-bold text-white bg-brand-accent hover:bg-red-700 transition"
            >
              Play With Friends
            </button>
            <button
              onClick={() => navigate("/solo")}
              className="flex-1 py-3 rounded-xl font-bold text-red-700 border-2 border-red-200 hover:bg-red-50 transition"
            >
              Play Solo
            </button>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Frequently asked questions</h2>
          <div className="space-y-4">
            {FAQS.map((f) => (
              <div key={f.q}>
                <h3 className="font-semibold text-gray-800 text-sm mb-1">{f.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
